// src/pages/utils/analyticsStateHooks.js
import { useCallback, useMemo, useRef } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useUser } from '../../context/UserContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const DEBOUNCE_MS = 1000;

export const ANALYTICS_STATE_VERSION = 1;

/** Default v1 shape — matches backend analytics_state.v1 contract */
export const DEFAULT_ANALYTICS_STATE = {
  version: ANALYTICS_STATE_VERSION,
  selectedCommodity: '',
  dateStart: '',
  dateEnd: '',
  widgetOptions: {},
  selections: {},
};

const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

/**
 * Remove undefined values (Firestore doesn't accept undefined)
 */
const sanitizeForFirestore = (value) => {
  if (Array.isArray(value)) {
    return value.map(sanitizeForFirestore);
  }
  if (value && typeof value === 'object') {
    const cleaned = {};
    for (const [key, v] of Object.entries(value)) {
      if (v !== undefined) {
        cleaned[key] = sanitizeForFirestore(v);
      }
    }
    return cleaned;
  }
  return value;
};

export const normalizeAnalyticsState = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_ANALYTICS_STATE };
  }
  return {
    ...DEFAULT_ANALYTICS_STATE,
    ...raw,
    version:
      typeof raw.version === 'number' && raw.version >= 1 ? raw.version : ANALYTICS_STATE_VERSION,
    selectedCommodity: typeof raw.selectedCommodity === 'string' ? raw.selectedCommodity : '',
    dateStart: typeof raw.dateStart === 'string' ? raw.dateStart : '',
    dateEnd: typeof raw.dateEnd === 'string' ? raw.dateEnd : '',
    widgetOptions: raw.widgetOptions && typeof raw.widgetOptions === 'object' ? raw.widgetOptions : {},
    selections: raw.selections && typeof raw.selections === 'object' ? raw.selections : {},
  };
};

/**
 * Firestore-backed Analytics view state (filters, dates, per-widget options).
 * Path: userPreferences/{email}/analytics/state
 */
export const useAnalyticsState = () => {
  const { email } = useUser();
  const queryClient = useQueryClient();
  const debouncedFlushRef = useRef(null);
  /** Latest save impl (email/queryClient/saveMutation); debounce wrapper calls this with no args */
  const flushAnalyticsSaveRef = useRef(() => {});

  const getDocRef = useCallback(() => {
    if (!email) return null;
    return doc(db, 'userPreferences', email, 'analytics', 'state');
  }, [email]);

  const {
    data: savedState,
    isPending,
    isLoading,
    isFetched,
    isError,
    error,
  } = useQuery({
    queryKey: ['analyticsState', email],
    queryFn: async () => {
      const docRef = getDocRef();
      if (!docRef) return DEFAULT_ANALYTICS_STATE;

      try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          return normalizeAnalyticsState(docSnap.data());
        }
        return DEFAULT_ANALYTICS_STATE;
      } catch (err) {
        console.error('Error fetching analytics state:', err);
        return DEFAULT_ANALYTICS_STATE;
      }
    },
    enabled: !!email,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: async (newState) => {
      const docRef = getDocRef();
      if (!docRef) {
        console.warn('Cannot save analytics state: no user email');
        return;
      }

      const payload = sanitizeForFirestore({
        ...newState,
        lastUpdated: serverTimestamp(),
      });

      await setDoc(docRef, payload, { merge: true });
    },
    // Do not invalidate here: optimistic cache is already correct. Invalidation refetches
    // and can churn context value + chart persist effects (infinite update loops).
    onError: (err) => {
      console.error('Error saving analytics state:', err);
    },
  });

  flushAnalyticsSaveRef.current = () => {
    if (!email) return;
    const merged = queryClient.getQueryData(['analyticsState', email]);
    if (merged) saveMutation.mutate(merged);
  };

  if (!debouncedFlushRef.current) {
    debouncedFlushRef.current = debounce(() => {
      flushAnalyticsSaveRef.current();
    }, DEBOUNCE_MS);
  }
  const scheduleAnalyticsSave = debouncedFlushRef.current;

  const state = savedState ?? DEFAULT_ANALYTICS_STATE;

  const updateStateOptimistically = useCallback(
    (partial) => {
      if (!email) return;
      queryClient.setQueryData(['analyticsState', email], (old) => {
        const current = old ?? DEFAULT_ANALYTICS_STATE;
        return normalizeAnalyticsState({
          ...current,
          ...partial,
          version: ANALYTICS_STATE_VERSION,
        });
      });
      scheduleAnalyticsSave();
    },
    [email, queryClient, scheduleAnalyticsSave]
  );

  const setWidgetOptions = useCallback(
    (widgetId, partial) => {
      if (!email) return;
      const key = ['analyticsState', email];
      queryClient.setQueryData(key, (old) => {
        const current = old ?? DEFAULT_ANALYTICS_STATE;
        const prev = current.widgetOptions?.[widgetId] || {};
        const nextWidgetOptions = {
          ...current.widgetOptions,
          [widgetId]: { ...prev, ...partial },
        };
        return normalizeAnalyticsState({
          ...current,
          widgetOptions: nextWidgetOptions,
          version: ANALYTICS_STATE_VERSION,
        });
      });
      scheduleAnalyticsSave();
    },
    [email, queryClient, scheduleAnalyticsSave]
  );

  const updateSelections = useCallback(
    (partial) => {
      if (!email) return;
      queryClient.setQueryData(['analyticsState', email], (old) => {
        const current = old ?? DEFAULT_ANALYTICS_STATE;
        return normalizeAnalyticsState({
          ...current,
          selections: { ...current.selections, ...partial },
          version: ANALYTICS_STATE_VERSION,
        });
      });
      scheduleAnalyticsSave();
    },
    [email, queryClient, scheduleAnalyticsSave]
  );

  return useMemo(
    () => ({
      state,
      /** True until the first `getDoc` result is applied (including default shape when the doc is missing). Prefer this over `isLoading` for gating UI. */
      isPending,
      /**
       * v5: `isLoading` === `isPending && isFetching` — it is false while still pending if the query
       * is not actively fetching (e.g. brief idle windows). Do not use alone to mean "server state ready".
       */
      isLoading,
      isFetched,
      isError,
      error,
      isSaving: saveMutation.isPending,
      updateState: updateStateOptimistically,
      setWidgetOptions,
      updateSelections,
    }),
    [
      state,
      isPending,
      isLoading,
      isFetched,
      isError,
      error,
      saveMutation.isPending,
      updateStateOptimistically,
      setWidgetOptions,
      updateSelections,
    ]
  );
};

export default useAnalyticsState;
